<script setup lang="ts">
const { $t } = useNuxtApp()
const discountCodeStore = useDiscountCodeStore()
const gtm = useGTM()
const appliedCode = ref('')
const error = ref('')
const textField = ref<HTMLInputElement>()

async function apply() {
  error.value = ''

  try {
    await discountCodeStore.applyDiscountCode(appliedCode.value)
    gtm.pushOrderDiscountCode(appliedCode.value)
    gtm.pushCartUpdate()
    appliedCode.value = ''
  }
  catch (e) {
    console.warn(e)
    error.value = $t('discountCodeIsInvalid')
  }
}

function removeDiscountCode() {
  discountCodeStore.removeDiscountCode()
}
</script>

<template>
  <form
    class="discount"
    @submit.prevent="apply"
  >
    <input
      ref="textField"
      v-model.trim="appliedCode"
      type="text"
      name="code"
      :placeholder="$t('discountCode')"
      required
      @focus="error === ''"
    >
    <ButtonBasic
      design="radius-without-border"
      background-color="var(--yellow)"
      native-type="submit"
      data-test="apply-discount-code-btn"
      :aria-label="$t('applyDiscountCode')"
      @click="apply"
    >
      {{ $t('apply') }}
    </ButtonBasic>
    <div aria-live="polite">
      <ErrorMessage
        v-show="error"
        color="var(--red)"
        :text="error"
      />
    </div>
    <span class="text-info">{{ $t('discountHint') }}</span>
    <div role="status">
      <div
        v-if="discountCodeStore.code"
        class="message successful"
      >
        <span class="message-text">
          {{ discountCodeStore.code }} {{ $t('discountCodeApplied') }}
        </span>
        <IconBasic
          filename="close"
          background-color="black"
          @click="removeDiscountCode"
        />
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@import 'assets/scss/rules/breakpoints';
@import 'assets/scss/typography/body';

.discount {
  flex-shrink: 0;
}

.text-info {
  font-size: 1.1rem;
  grid-column: span 2;
  margin-top: 0.8rem;
}

form {
  display: grid;
  align-items: end;
  button {
    max-width: 80%;
    grid-column: 2;
    justify-self: end;
    padding: 0.94rem 1.6rem;

    span {
      font-size: 1.4rem;
    }
  }
}

input {
  width: 100%;
  max-width: 100%;
  grid-column: 1;
  font-size: 1.4rem;
  border: 1px solid var(--gray-border);
  padding: 0.8rem 1.6rem;
  min-height: 4rem;

  @media screen and (max-width: $phone-bigger) {
    font-size: 1.6rem;
  }

  &:focus {
    border: 2px solid var(--black);
    outline: none;
  }
}

.message {
  @include body2;
  grid-column: span 2;
  margin-top: 0.5rem;
  margin-bottom: 0rem;
  background-color: var(--yellow);
  border-radius: 2rem;
  padding: 0.8rem 1.6rem;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;

  @media screen and (min-width: $tablet) {
    align-items: center;
  }

  .message-text {
    max-width: 85%;
  }

  span.icon {
    width: 0.8rem;
    height: 0.8rem;
    cursor: pointer;
    position: absolute;
    right: 1rem;
    margin: 0.8rem 1rem;
  }

  &.successful {
    margin-top: 2.4rem;
  }
}
</style>
